import {
    Material,
    Mesh,
    MeshAttribute,
    MeshComponent,
    Object3D,
    Texture,
} from '@wonderlandengine/api';
import {
    Align,
    Display,
    FlexDirection,
    Justify,
    TextProps,
} from '@wonderlandengine/react-ui';
import {
    Container,
    Image,
    MaterialContext,
    Panel9Slice,
    Text,
} from '@wonderlandengine/react-ui/components';

import React, {useRef, useMemo, useEffect, useCallback, useState, useContext} from 'react';
import {parseColor, resizeLabelToFit} from '../utils/utils.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Animation, loop, play, stop} from '../utils/animation-context.js';
import {RotatableImage} from './rotatable-image.js';
import {Sounds} from '../../enums/Sounds.js';
import {AudioManager} from '@wonderlandengine/spatial-audio';
import {AudioDirector} from '../../components/audio-director.js';
import {useMemoMaterial} from '../utils/MaterialPool.js';
import {FlatOpaqueTextured, UIText} from '../../types/materials.js';

const tempPosLength = 32;
const tempPositions = new Float32Array(3 * tempPosLength);

/** Get bounding box of a mesh */
function calculateMeshBoundingBox(m: Mesh, min: Float32Array, max: Float32Array) {
    if (m.vertexCount === 0) return;

    const pos = m.attribute(MeshAttribute.Position);

    pos.get(0, min);
    max.set(min);

    for (let i = 1; i < pos.length; i += tempPosLength) {
        const p = pos.get(i, tempPositions);

        const end = Math.min(tempPosLength, pos.length - i);
        for (let x = 0; x < end; ++x) {
            min[0] = Math.min(p[x * 3], min[0]);
            min[1] = Math.min(p[x * 3 + 1], min[1]);
            min[2] = Math.min(p[x * 3 + 2], min[2]);

            max[0] = Math.max(p[x * 3], max[0]);
            max[1] = Math.max(p[x * 3 + 1], max[1]);
            max[2] = Math.max(p[x * 3 + 2], max[2]);
        }
    }
}

function calculateFontSize(props: React.PropsWithChildren<TextProps>) {
    let fontsize = props.fontSize ?? 32;

    if (typeof props.width === 'number') {
        let text = props.text ?? props.children.toString() ?? '';
        fontsize = resizeLabelToFit(text, props.width, fontsize);
    }
    return fontsize;
}

/**
 * Hook to get the bounding box of the first mesh on the first mesh
 * component of a object ref.
 *
 * Limitations: When the mesh updates, the bounding box is not recalcuated.
 */
function getBoundingBox(
    ref: React.MutableRefObject<Object3D>,
    min: Float32Array,
    max: Float32Array
) {
    if (!ref.current) return;

    const c = ref.current.findByNameDirect('mesh')[0];
    const m = c?.getComponent(MeshComponent);
    if (m && m.mesh) {
        calculateMeshBoundingBox(m.mesh, min, max);
    }
}

export const MenuButton = (
    props: React.PropsWithChildren<TextProps> & {
        color?: string;
        icon?: string | Texture;
        iconWidth?: number;
        iconHeight?: number;
        animate?: boolean;
        alignIcon?: 'left' | 'right';
        rotation?: number;
        sound?: Sounds;
        /**
         * If true, the text can be centered without taking the icon into account.
         * This is useful for when buttons with and without icons are mixed and
         * the text should be centered in all cases.
         */
        ignoreIconForCentering?: boolean;
    }
) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);

    const shadowMaterial = useMemoMaterial('shadow', () => rotbMats?.buttonShadowMaterial);
    const ref = useRef<Object3D>(null);

    const buttonBackgroundMaterial = useMemoMaterial(
        `menuButton_${props.color ?? '#ffffff'}`,
        () => {
            const m = rotbMats?.buttonMaterial.clone() as FlatOpaqueTextured;
            m.setColor(parseColor(props.color ?? '#ffffff'));
            return m;
        },
        [props.color]
    );

    const buttonTextMaterial = useMemoMaterial(
        `menuButtonText`,
        () => {
            return mats?.textMaterial?.clone() as UIText;
        },
        [props]
    );

    const buttonText = useMemo(
        () => props?.text || props?.children?.toString() || '',
        [props.text, props.children]
    );

    const min = useMemo(() => new Float32Array(3), []);
    const max = useMemo(() => new Float32Array(3), []);

    /* Play animation if requested */
    useEffect(() => {
        if (!props.animate) return;
        let boundingBoxSet = false;
        const anim = loop((f) => {
            if (!ref.current) return;
            if (!boundingBoxSet) {
                getBoundingBox(ref, min, max);
                boundingBoxSet = true;
            }
            const a = 0.02;
            const o = 1 - 2 * a;
            const x = a * Math.sin(f * 2 * Math.PI);
            const sx = o + x;
            const sy = o - x;
            ref.current.setScalingLocal([sx, sy, 1]);
            ref.current.setPositionLocal([-min[0] * (1 - sx), min[1] * (1 - sy), 0]);
        }, 1);
        return () => stop(anim);
    }, [props.animate]);

    const downAnim = useRef<Animation>(null);
    const handleOnDown = (e: any) => {
        getBoundingBox(ref, min, max);
        /* Press-animation */
        downAnim.current = play(
            (f: number) => {
                if (!ref.current) return;
                const a = 0.1;
                const x = a * f;
                const s = 1 - x;
                ref.current.setScalingLocal([s, s, 1]);
                ref.current.setPositionLocal([-min[0] * (1 - s), min[1] * (1 - s), 0]);
            },
            0.1,
            () => (downAnim.current = null)
        );
    };

    return (
        <Container
            {...props}
            display={Display.Flex}
            justifyContent={Justify.Center}
            alignContent={Align.Center}
            onDown={handleOnDown}
            onUp={(e: any) => {
                // stop the down animation if it's running
                downAnim.current && stop(downAnim.current);
                // reset the button
                ref.current?.resetPosition();
                ref.current?.resetScaling();

                AudioDirector.instance.playOneShot(props.sound ?? Sounds.confirm, true);
            }}
            onUnhover={(e: any) => {
                // stop the down animation if it's running
                downAnim.current && stop(downAnim.current);
                // reset the button
                ref.current?.resetPosition();
                ref.current?.resetScaling();
            }}
        >
            {/* Root for animation, relative the component root */}
            <Container left={4} top={6}>
                <Panel9Slice
                    ref={ref}
                    borderSize={8}
                    borderTextureSize={0.0625}
                    material={shadowMaterial}
                >
                    <Panel9Slice
                        height={props.height}
                        paddingLeft={props.text || props.children ? 12 : 0}
                        left={-4}
                        //  top={-6}
                        material={buttonBackgroundMaterial}
                        borderSize={8}
                        borderTextureSize={0.0625}
                        flexDirection={FlexDirection.Row}
                        alignItems={Align.Center}
                        alignContent={Align.Center}
                        justifyContent={Justify.Center}
                    >
                        {props.icon && (!props.alignIcon || props.alignIcon == 'left') && (
                            <RotatableImage
                                height={props.iconHeight ?? 30}
                                width={props.iconWidth ?? 30}
                                rounding={0}
                                src={props.icon!}
                                rotation={props.rotation ?? 0}
                            ></RotatableImage>
                        )}
                        {buttonText && (
                            <Text
                                top={4}
                                left={-4}
                                textAlign={props.textAlign ?? 'center'}
                                flexGrow={1}
                                // top={-(props.fontSize ?? 32) / 2}
                                fontSize={calculateFontSize(props)}
                                marginRight={
                                    props.ignoreIconForCentering && props.icon
                                        ? (props.iconWidth ?? 30)
                                        : 0
                                }
                                material={buttonTextMaterial}
                                text={buttonText}
                            ></Text>
                        )}
                        {props.icon && props.alignIcon == 'right' && (
                            <Image
                                height={props.iconHeight ?? 30}
                                width={props.iconWidth ?? 30}
                                rounding={0}
                                src={props.icon!}
                                marginRight={15}
                            ></Image>
                        )}
                    </Panel9Slice>
                </Panel9Slice>
            </Container>
        </Container>
    );
};
